import {useEffect, useState} from "react";

type FetchFactory<T> = () => Promise<T>;
type OnOk<T> = ((result: T) => void) | undefined;
type OnErr = ((err: any) => void) | undefined;

export function useFetch<T>(fetchFactory: FetchFactory<T>, onOk: OnOk<T> = undefined, onErr: OnErr = undefined, deps: any[] = []) {
    useEffect(() => {
        const controller = new AbortController();
        const {signal} = controller;
        fetchFactory().then(result => {
            if (!signal.aborted) {
                onOk?.(result);
            }
        }).catch((error: any) => {
            if (signal.aborted) return;

            if (onErr) {
                onErr(error);
            } else {
                throw error;
            }
        });
        return () => controller.abort();
    }, deps); // eslint-disable-line react-hooks/exhaustive-deps
}

export function useFetchWithLoading<T>(fetchFactory: FetchFactory<T>, onOk: OnOk<T> = undefined, onErr: OnErr = undefined, deps: any[] = []) {
    const [isLoading, setIsLoading] = useState(true);
    useFetch(() => {
        setIsLoading(true);
        return fetchFactory().finally(() => setIsLoading(false));
    }, onOk, onErr, deps);
    return isLoading;
}

export type NotLoadedYet<T> = {
    status: "loading",
}

export type Loaded<T> = {
    status: "loaded",
    data: T,
}

export type LoadedWithError<T> = {
    status: "error",
    error: any,
}

export type Loading<T> = Loaded<T> | NotLoadedYet<T> | LoadedWithError<T>;
export type LoadingStatus = Loading<any>["status"];
export type LoadingRaw<T> = { data: T | undefined, error: any, status: LoadingStatus };

export function useFetchWithLoadingSmart<T>(fetchFactory: FetchFactory<T>, deps: any[] = []): Loading<T> {
    const [data, setData] = useState<LoadingRaw<T>>(
        {data: undefined, error: undefined, status: "loading"}
    );
    useFetch(
        fetchFactory,
        data => {
            setData({data, error: undefined, status: "loaded"});
        },
        err => {
            setData({data: undefined, error: err, status: "error"});
        },
        deps
    );
    return data as Loading<T>;
}